'use client'

import { useLocale } from 'next-intl'
import { Ref, startTransition, useEffect, useState } from 'react'

import { StyledRelativeTimeDisplay } from '../styled/relative-time-display'
import { Tooltip } from '@radix-ui/themes'
import { TextProps } from '@radix-ui/themes'

type TProps = {
    ref?: Ref<HTMLSpanElement | null>
    timestamp: Date
}

const getRelativeLocaleTime = (locale: string, timestamp: Date, reference: Date = new Date()) => {
    const units = [
        { limit: 60, divisor: 1, unit: 'seconds' as const },
        { limit: 60, divisor: 60, unit: 'minutes' as const },
        { limit: 24, divisor: 60, unit: 'hours' as const },
        { limit: Infinity, divisor: 24, unit: 'days' as const },
    ]

    let diff = (timestamp.getTime() - reference.getTime()) / 1000
    let unit = units[0].unit

    // `divisor` converts the previous unit into this one
    for (const { limit, divisor, unit: currentUnit } of units) {
        diff /= divisor
        if (Math.abs(diff) < limit) {
            unit = currentUnit
            break
        }
    }

    return new Intl.RelativeTimeFormat(locale, { style: 'short' }).format(Math.trunc(diff), unit)
}

const RelativeTimeDisplay = ({ timestamp, ref, ...textProps }: TextProps & TProps) => {
    const locale = useLocale()
    const [text, setText] = useState<string | null>(null)

    useEffect(() => {
        const update = () => startTransition(() => {
            setText(getRelativeLocaleTime(locale, timestamp))
        })

        update()
        const interval = setInterval(update, 1000)
        return () => clearInterval(interval)
    }, [locale, timestamp])

    return (
        <Tooltip content={timestamp.toLocaleString()}>
            <StyledRelativeTimeDisplay {...textProps} ref={ref}>
                {text ?? '…'}
            </StyledRelativeTimeDisplay>
        </Tooltip>
    )
}

export default RelativeTimeDisplay
